import * as _ from 'underscore';
import { FavoriteItemType } from '../ServerApi/types/IFavorite';

import { ISchedule } from '../../schedule/types/ISchedule';
import { ISiteNameWithId } from '../ServerApi/types/ISiteNameWithId';
import { ExplorableItemType, SpaceInfoItemType } from './ContentType';
import { ExtractEncryptionState } from './IDataSourceInfo';
import { IUserInfo } from './IUserInfo';
import { FlowDraftItemInfo } from './FlowDraftInfo';
import { ILocationInfo } from './ILocationInfo';

export interface ICertifiableItemInfo {
  isCertified?: boolean;
  certificationNote?: string;
  certifier?: IUserInfo;
}

export interface ICatalogCertifiableItemInfo {
  isCertified?: boolean;
  certificationNote?: string;
  certifier?: ICatalogUserInfo;
}

export interface ICreatedItemInfo {
  createdAt: Date;
}

export interface IDescriptionItemInfo {
  description: string;
}

export interface IFavoriteItem {
  favorite: boolean;
}

export interface IRecommendationItem {
  hide?: boolean;
  readonly modelId?: string;
  readonly explanationType?: string;
  readonly similiarUsers?: IUserInfo[];
}

export interface FlowItemInfo {
  readonly flowId: string;
}

export interface FlowItemWithGraphImageInfo {
  readonly flowGraphImageUrl?: string;
}

export interface FlowItemWithFileTypeInfo {
  readonly flowFileType: string;
}

export interface FlowItemWithDocumentVersionInfo {
  readonly flowDocumentVersion: string;
}

export interface FlowItemWithThumbnailInfo {
  readonly thumbnailUrl?: string;
}

export interface FlowDraftInfo {
  flowDraft: FlowDraftItemInfo;
}

export interface DraftStatusInfo {
  draftStatus?: string;
}

export interface DraftIdInfo {
  draftId?: string;
}

export interface DraftLuidInfo {
  draftLuid?: string;
}

export interface IIdItemInfo {
  id: string;
}

export interface ILuidItemInfo {
  luid?: string;
}

export interface INamedItemInfo extends IIdItemInfo, ILuidItemInfo, IContentsItemInfo {
  name?: string;
  count?: number;
}

export interface ITypedItemInfo extends INamedItemInfo {
  type: string;
}

export interface IContentsItemInfo {
  contents?: any;
}

export interface IExplorableItemTypeInfo<C extends ExplorableItemType = ExplorableItemType> extends INamedItemInfo, IVizportalIdItemInfo {
  contentType: C;
}

export interface IFavoritableItemInfo extends INamedItemInfo {
  contentType: FavoriteItemType;
  idForFavoriting?: string;
}

// SpaceInfo's item must extend this interface
export interface ISpaceTypeInfo<C extends SpaceInfoItemType = SpaceInfoItemType> extends INamedItemInfo {
  contentType: C; // this field is "contentType" rather than "spaceType" so that an IContentInfo can also be a ISpaceTypeInfo
}

export interface IOwnedItemInfo {
  owner?: IUserInfo;
}

export interface IModifiedByItemInfo {
  modifiedBy?: IUserInfo;
}

// A contact is different from an owner in that he/she doesn't own the asset (applicable to database & table content types).
export interface IContactItemInfo {
  contact?: IUserInfo;
}

export interface ICatalogContactItemInfo {
  contact?: ICatalogUserInfo;
}

export interface IProjectItemInfo {
  project?: INamedItemInfo;
}


export interface ILocationItemInfo {
  location?: ILocationInfo;
}

export function isIProjectItemInfo(x: any): x is IProjectItemInfo {
  return x.hasOwnProperty('project');
}

export interface ITaggedItemInfo {
  tags: string[];
}

export interface IThumbnailItemInfo {
  thumbnailUrl: string;
}

export interface IDownloadItemInfo {
  downloadUrl: string;
}

export interface IUpdatedItemInfo {
  updatedAt: Date;
}

export interface IEditableItemInfo {
  // This property will be undefined if the workbook is not editable, example: workbooks with stories
  editUrl?: string;
}

export interface ITabbedItemInfo {
  displayTabs: boolean;
}

export interface IItemStatisticsInfo<T extends _.Dictionary<number>> {
  usageInfo: T; // The statistics for this particular item
  usageInfoMaxValues: T; // The maximum values of each statistic across all items in the relevant set
}

export interface IStatsTimeSeriesInfo {
  hitsTimeSeries?: number[];
}

export interface IEncryptableExtractItemInfo {
  hasExtracts: boolean;
  extractEncryptionState: ExtractEncryptionState;
  hasEncryptableExtracts: boolean;
}

export interface IExtractItemInfo extends IEncryptableExtractItemInfo {
  name?: string;
  lastRefreshedAt?: Date;
  hasAlert: boolean;
  hasIncrementalExtract: boolean;
  extractCreationPending: boolean;
}

export interface IScheduledItemInfo {
  schedule: ISchedule;
}

export interface ISiteItemInfo {
  site: ISiteNameWithId;
}

export interface INamedChildItemInfo extends INamedItemInfo {
  parentId: string;
}

// TFS912863: Remove this hack once we switched to use vizportalId as primary ID for database & table
export interface IVizportalIdItemInfo {
  vizportalId?: string;
}

export interface IControlledPermissionItemInfo extends INamedItemInfo, IIdItemInfo {
  controlledPermissionsEnabled: boolean;
  nestedProjectsPermissionsIncluded: boolean;
}

export interface IRelatedContentInfo {
  relatedContentId: string;
  relatedContentType: ExplorableItemType;
}

export interface ISuspendedItem {
  suspended: boolean;
}

export interface IDownstreamWorkbooksConnectionInfo {
  __typename?: 'WorkbooksConnection';
  totalCount: number;
}

export class IDownstreamDatasourcesConnectionInfo {
  __typename?: 'DatasourcesConnection';
  totalCount: number;
}

export interface IDataQualityWarningAuthorInfo {
  __typename?: 'TableauUser';
  username: string;
  name: string;
  domain: string;
}

export interface ICatalogUserInfo extends IIdItemInfo{
  __typename?: 'TableauUser';
  username: string;
  name: string;
  domain: string;
  readOnly?: boolean;
  hasServerDisplay?: boolean;
}

export interface IDataQualityWarningInfo {
  __typename?: 'DataQualityWarning';
  author: IDataQualityWarningAuthorInfo;
  authorDisplayName?: string | null;
  isActive: boolean;
  isSevere: boolean;
  owner: IDataQualityWarningAuthorInfo;
  message: string;
  updatedAt?: Date;
  vizportalId: string;
  warningType: string;
  assetUrl: any;
  assetName: string;
  assetType: string;
}

export interface IExtractDraftInfo {
  extractDraftAvailable?: boolean;
  editExtractDraftUrl?: string;
}

export interface ITableDatabaseInfo {
  __typename: string;
  name: string;
  hostName?: string;
  isGrouped: boolean;
}

export interface ITableColumnInfo {
  name: string;
}

export interface ITableColumnsConnectionInfo {
  totalCount: number;
  nodes: ITableColumnInfo[];
}

export interface ITagInfo {
  __typename: string;
  name: string;
}